'use strict';

/**
 * View/Layout for create/edit object
 *
 * @category   View
 * @module     edit
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var BaseLayout = require('./base');
var ObjectModel = require('modules/collection/models/object');

var CreateLayout = BaseLayout.extend({

    /**
     * Basic mode. Can be either 'create' or 'edit'
     * @type {string}
     */
    mode: 'create',

    /**
     * Regions are created dynamically, beside of object id
     * @type {Object}
     */
    regions: {
        objectidSelectionRegion: '#region-objectid-selection',
    },

    ui: function() {
        return _.extend(BaseLayout.prototype.ui.apply(this, arguments), {
            submitSimilar: '[data-action="submit-similar"]',
        });
    },

    events: function() {
        return _.extend(BaseLayout.prototype.events.apply(this, arguments), {
            'click @ui.submitSimilar': 'submitSimilarClicked',
        });
    },

    /**
     * Indicates, if the user want's to add another model with the same properties
     * @type {boolean}
     */
    addSimilar: false,

    /**
     * Makes a soft reset, when the user wants to add a similar object
     */
    resetSimilar: function() {
        this.model.unset('id', {silent: true});

        _.each(this.fields, function(field){
            if (field.options && field.options.resetOnDuplicate) {
                var fieldView = this.getRegion('field' + _.ucfirst(field.name) + 'Region').currentView;

                this.model.unset(field.name, {silent: true});

                if (fieldView && _.isFunction(fieldView.reset)) {
                    fieldView.reset();
                } else {
                    console.warn('Missing reset() function for edit component: ', field.name);
                }
            }
        }.bind(this))

        // Reset states
        this.addSimilar = false;
        this.hasFormChanged = false;
        this.checkRequired();
    },

    /**
     * Submit was clicked, we activate the form submission
     * @param event
     */
    submitSimilarClicked: function(event) {
        if (!this.ui.submitSimilar.prop('disabled')) {
            this.setSubmitState(false, true);

            _.delay(function() {
                this.addSimilar = true;
                this.ui.form.submit();
            }.bind(this), 20);
        }
    },

    /**
     * Event for submitting the form
     * @param {object} event The event object
     * @return {void}
     */
    formSubmit: function(event) {
        var data = this.getData();

        var setIds = data.setIds;
        delete data.setIds;

        // Debug data
        Colido.config.debug && console.info(data);

        //Do not use collection.create() because the Websocket might be faster adding a duplicate model.
        // Problem is:
        // - create directly adds a model without ID into the collection
        // - then the WS might add a duplicate model with an ID to the collection (existing check needs an ID to work)
        // - then create:success fires and updates the existing model with the ID from the server
        // - resulting in too models with the same id in the collection

        //Needed, so model can use collection url with catalog id
        this.model.collection = this.collection;

        this.model.save(data, {
            success: function(model, response, options) {
                this.collection.add(model);
                var catalog = this.collection.catalog;
                var objectId = model.get('id');

                // Update sets
                //@todo if objects have setIds move this into objectCollection.onAdd listener in sets collection (see locations collections for details)
                if (_.isArray(setIds)) {
                    setIds.forEach(function(setId) {
                        var setModel = catalog.sets.get(setId);
                        setModel.addItems(objectId);
                    });
                }

                var itemLink = App.html.link({
                    url: '/catalogs/' + catalog.id + '/show/' + objectId,
                    label: '»' + _.escape(model.get('properName')) + '«',
                });

                // Trigger creation of object
                App.vent.trigger('collection:object-created', model);

                if (this.addSimilar) {
                    this.resetSimilar();

                    Messenger().post({
                        type: 'success',
                        message: App.t('collection::create:response:success:create-and-duplicate', {itemLink: itemLink}),
                    });

                    this.setSubmitState(true, false);
                    this.ui.scrollContainer.animate({scrollTop: 0}, 400);
                } else {
                    this.wasSaved = true;

                    this.navigateBack();

                    Messenger().post({
                        type: 'success',
                        message: App.t('collection::create:response:success:create', {itemLink: itemLink}),
                    });
                }
            }.bind(this),

            error: function(model, response, options) {
                this.setSubmitState(true, false);
                this.addSimilar = false;

                var errorMessage = Messenger().post({
                    type: 'error',
                    message: App.t('collection::create:response:error:create'),
                    actions: {
                        'try-again': {
                            label: App.t('global::messenger:button:try-again'),
                            action: function() {
                                errorMessage.hide();
                                this.ui.form.submit();
                            }.bind(this),
                        },
                        'cancel': {
                            label: App.t('global::messenger:button:cancel'),
                            action: function() {
                                errorMessage.hide();
                            }.bind(this),
                        },
                    },
                });
            }.bind(this),
        });

        event.preventDefault();
    },

    navigateBack: function () {
        // Default to dashboard
        var url = 'dashboard';

        // Find last route that wasn't the create dialog
        var router = this.getRouter();
        var history = router.history;
        for (var i = history.length - 1; i >= 0; i--) {
            if (!history[i].match(/.*\/new$/) && !history[i].match(/.*\/duplicate\//)) {
                url = history[i];
                break;
            }
        }

        router.navigate(url, {trigger: true});
    },

    getRouter: function () {
        return App.router;
    },

    /**
     * Event for submitting the form
     * @param {object} event The event object
     * @return {void}
     */
    formReset: function(event) {
        event.preventDefault();

        this.trigger('close:modal', {wasCancled: true});
    },
});

module.exports = CreateLayout;